import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, MessageCircle, Phone, Mail, User, HelpCircle } from "lucide-react";
import { portal } from "../lib/api";
import { restartClientTour } from "./ClientWelcomeTour";

/**
 * ClientAgentBubble — floating profile-picture FAB on the client portal.
 * Tapping it opens a small popover with a "Message your agent" CTA that
 * jumps the client to their current transaction's message thread (that's
 * where the existing chat lives). Falls back to phone/email if no
 * transaction exists yet.
 *
 * z-index sits at z-[70] — the same tier as agent-side support so it
 * survives dialogs and the ClientWelcomeTour overlay.
 */
export default function ClientAgentBubble({ agent }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [tx, setTx] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [unread, setUnread] = useState(0);

  // Lazily fetch the client's active transaction so we know where to link
  // the "Message your agent" CTA. Not fatal if it fails — we degrade to
  // phone/email links.
  useEffect(() => {
    portal.get("/portal/transactions")
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : [];
        // Prefer an active transaction (not closed/cancelled/expired).
        const active = list.find(t => !["closed", "cancelled", "expired"].includes(t.status));
        setTx(active || list[0] || null);
      })
      .catch(() => setTx(null))
      .finally(() => setLoaded(true));
  }, []);

  // Poll the unread count so a red dot appears when the agent has sent
  // a new message. 30s interval balances freshness with backend load.
  // Also runs immediately on mount and after the popover closes (which
  // is likely right after the client navigated to the chat and read them).
  useEffect(() => {
    let stopped = false;
    const load = async () => {
      try {
        const { data } = await portal.get("/portal/messages/unread-count");
        if (!stopped) setUnread(data?.unread || 0);
      } catch { /* silent */ }
    };
    load();
    const id = setInterval(load, 30_000);
    return () => { stopped = true; clearInterval(id); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!agent) return null;

  const initials = (agent.full_name || "?")
    .split(" ").filter(Boolean).slice(0, 2)
    .map(s => s[0].toUpperCase()).join("");

  const goToChat = () => {
    // Always route to the General thread — works even before any transaction
    // exists. The client can jump into a tx-specific thread from the
    // transaction page itself when they want deal-scoped context.
    nav(`/portal/messages`);
    setOpen(false);
  };

  return (
    <>
      {open && (
        <div
          className="fixed bottom-24 right-6 z-[70] w-[320px] rounded-xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
          role="dialog"
          aria-label={`Contact ${agent.full_name || "your agent"}`}
          data-testid="portal-agent-popover"
        >
          <div
            className="relative px-4 pt-5 pb-4"
            style={{ background: "linear-gradient(135deg, var(--primary-hex) 0%, var(--accent-hex) 100%)" }}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-2 right-2 h-7 w-7 rounded-full text-white/80 hover:text-white hover:bg-white/10 flex items-center justify-center"
              aria-label="Close"
              data-testid="portal-agent-popover-close"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-3">
              {agent.photo_url ? (
                <img
                  src={agent.photo_url}
                  alt={agent.full_name}
                  className="h-12 w-12 rounded-full border-2 border-white shadow-md object-cover shrink-0"
                />
              ) : (
                <div className="h-12 w-12 rounded-full border-2 border-white shadow-md bg-white/20 flex items-center justify-center text-white font-bold shrink-0">
                  {initials || <User className="h-6 w-6" />}
                </div>
              )}
              <div className="min-w-0 text-white">
                <div className="text-[10px] font-semibold uppercase tracking-widest opacity-80">Your agent</div>
                <div className="font-heading text-base font-bold tracking-tight truncate">{agent.full_name || "Your agent"}</div>
              </div>
            </div>
          </div>
          <div className="p-3 space-y-1">
            <button
              type="button"
              onClick={goToChat}
              disabled={!loaded}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-slate-50 text-left disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="portal-agent-chat"
            >
              <div className="relative h-8 w-8 rounded-full flex items-center justify-center text-white shrink-0" style={{ background: "var(--primary-hex)" }}>
                <MessageCircle className="h-4 w-4" />
                {unread > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 ring-2 ring-white text-[10px] font-bold text-white flex items-center justify-center" data-testid="portal-chat-unread-count">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900">
                  Send a message
                  {unread > 0 && <span className="ml-2 text-xs font-semibold text-rose-600">· {unread} new</span>}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {loaded ? "Chat with your agent" : "Loading…"}
                </div>
              </div>
            </button>
            {agent.phone && (
              <a
                href={`tel:${agent.phone.replace(/[^+0-9]/g, "")}`}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-slate-50"
                data-testid="portal-agent-phone-link"
              >
                <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  <Phone className="h-4 w-4 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900">Call</div>
                  <div className="text-xs text-slate-500 truncate">{agent.phone}</div>
                </div>
              </a>
            )}
            {agent.email && (
              <a
                href={`mailto:${agent.email}`}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-slate-50"
                data-testid="portal-agent-email-link"
              >
                <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  <Mail className="h-4 w-4 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900">Email</div>
                  <div className="text-xs text-slate-500 truncate">{agent.email}</div>
                </div>
              </a>
            )}
            <button
              type="button"
              onClick={() => { setOpen(false); restartClientTour(); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-slate-50 text-left border-t border-slate-100 mt-1 pt-3"
              data-testid="portal-restart-tour"
            >
              <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                <HelpCircle className="h-4 w-4 text-slate-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900">Replay the tour</div>
                <div className="text-xs text-slate-500 truncate">See how the portal works</div>
              </div>
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="fixed bottom-6 right-6 z-[70] h-14 w-14 rounded-full shadow-xl overflow-hidden ring-2 ring-white hover:scale-105 transition-transform focus:outline-none focus:ring-4 focus:ring-slate-900/20"
        aria-label={open ? "Close" : `Contact ${agent.full_name || "your agent"}${unread > 0 ? ` — ${unread} new message${unread === 1 ? "" : "s"}` : ""}`}
        aria-expanded={open}
        data-testid="portal-agent-bubble"
        style={{ background: "var(--primary-hex)" }}
      >
        {agent.photo_url ? (
          <img
            src={agent.photo_url}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center font-bold text-white">
            {initials || <User className="h-6 w-6" />}
          </span>
        )}
        {unread > 0 && !open && (
          <span
            className="absolute top-0 right-0 min-w-[22px] h-[22px] px-1.5 rounded-full bg-rose-500 ring-2 ring-white text-[11px] font-bold text-white flex items-center justify-center animate-pulse"
            data-testid="portal-agent-bubble-badge"
            aria-hidden="true"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
    </>
  );
}
